import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { promptsAPI, categoriesAPI, tokenAnalyzerAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FaSave, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

const EditPrompt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingPrompt, setFetchingPrompt] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tokenCount, setTokenCount] = useState(0);
  const [analyzingTokens, setAnalyzingTokens] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState(null);
  const [hasRatings, setHasRatings] = useState(false);
  const [showResetWarning, setShowResetWarning] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prompt_text: '',
    examples: '',
    category_id: '',
    tags: '',
    target_ai_model: 'gpt-4',
    is_public: true,
  });

  const fetchPromptData = useCallback(async () => {
    setFetchingPrompt(true);
    try {
      const response = await promptsAPI.getById(id);
      const prompt = response.data.prompt;

      // Check if user owns this prompt
      if (prompt.user_id !== userProfile?.id) {
        toast.error('You do not have permission to edit this prompt');
        navigate(`/prompts/${id}`);
        return;
      }

      setOriginalPrompt(prompt);
      setHasRatings(prompt.total_ratings > 0);
      setTokenCount(prompt.token_count || 0);

      setFormData({
        title: prompt.title || '',
        description: prompt.description || '',
        prompt_text: prompt.prompt_text || '',
        examples: prompt.examples || '',
        category_id: prompt.category_id || '',
        tags: Array.isArray(prompt.tags) ? prompt.tags.join(', ') : '',
        target_ai_model: prompt.target_ai_model || 'gpt-4',
        is_public: prompt.is_public !== false,
      });
    } catch (error) {
      console.error('Fetch prompt error:', error);
      toast.error('Failed to load prompt');
      navigate('/dashboard');
    } finally {
      setFetchingPrompt(false);
    }
  }, [id, navigate, userProfile]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
      toast.error('Failed to load categories');
    }
  }, []);

  useEffect(() => {
    fetchPromptData();
    fetchCategories();
  }, [fetchPromptData, fetchCategories]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleQuillChange = (content) => {
    setFormData({
      ...formData,
      prompt_text: content,
    });
    // Debounce token counting
    if (content && content.length > 10) {
      debouncedTokenCount(content);
    }
  };

  // Debounce function for token counting
  let tokenCountTimeout;
  const debouncedTokenCount = (text) => {
    clearTimeout(tokenCountTimeout);
    tokenCountTimeout = setTimeout(() => {
      analyzeTokens(text);
    }, 1000);
  };

  const analyzeTokens = async (text) => {
    // Remove HTML tags for token counting
    const plainText = text.replace(/<[^>]*>/g, '');
    if (!plainText.trim()) {
      setTokenCount(0);
      return;
    }

    setAnalyzingTokens(true);
    try {
      const response = await tokenAnalyzerAPI.count(plainText, formData.target_ai_model);
      setTokenCount(response.data.tokenCount || 0);
    } catch (error) {
      console.error('Token count error:', error);
    } finally {
      setAnalyzingTokens(false);
    }
  };

  const hasContentChanged = () => {
    return (
      formData.prompt_text !== originalPrompt?.prompt_text ||
      formData.title !== originalPrompt?.title ||
      formData.description !== originalPrompt?.description
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    if (!formData.prompt_text.trim()) {
      toast.error('Please enter the prompt text');
      return;
    }
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    // Check if major changes made and prompt has ratings
    if (hasRatings && hasContentChanged() && !showResetWarning) {
      setShowResetWarning(true);
      return;
    }

    setLoading(true);
    try {
      // Parse tags (comma-separated)
      const tagsArray = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const promptData = {
        title: formData.title,
        description: formData.description,
        promptText: formData.prompt_text,
        examples: formData.examples,
        categoryId: formData.category_id,
        tags: tagsArray,
        targetAiModel: formData.target_ai_model,
        isPublic: formData.is_public,
        token_count: tokenCount,
        resetRatings: hasRatings && hasContentChanged(), // Reset ratings if content changed
      };

      const response = await promptsAPI.update(id, promptData);

      if (response.data.ratingsReset) {
        toast.success('Prompt updated successfully! All ratings have been reset.');
      } else {
        toast.success('Prompt updated successfully!');
      }

      navigate(`/prompts/${id}`);
    } catch (error) {
      console.error('Update prompt error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update prompt';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'blockquote',
    'code-block',
    'link',
  ];

  if (fetchingPrompt) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">Loading prompt...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Edit Prompt</h1>
        <p className="text-gray-600">Update your prompt details</p>
      </div>

      {/* Reset Warning */}
      {showResetWarning && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
          <div className="flex items-start">
            <FaExclamationTriangle className="text-yellow-600 text-2xl mr-3 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-yellow-900 mb-2">Warning: Ratings Will Be Reset</h3>
              <p className="text-yellow-800 mb-4">
                You are making significant changes to this prompt (title, description, or content).
                This will <strong>permanently delete all {originalPrompt?.total_ratings} existing ratings</strong> and reset your prompt's score to 0.
              </p>
              <p className="text-yellow-800 mb-4">
                Consider:
              </p>
              <ul className="list-disc list-inside text-yellow-800 mb-4 space-y-1">
                <li>Creating a new prompt instead (fork this one)</li>
                <li>Only making minor edits if you want to keep ratings</li>
                <li>Waiting until you have fewer ratings</li>
              </ul>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResetWarning(false);
                    handleSubmit({ preventDefault: () => {} });
                  }}
                  className="bg-yellow-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-yellow-700 transition"
                >
                  Continue & Reset Ratings
                </button>
                <button
                  onClick={() => setShowResetWarning(false)}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <label className="block text-sm font-semibold mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g., 'GPT-4 Coding Assistant Prompt'"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            maxLength={200}
            required
          />
          <p className="text-sm text-gray-500 mt-1">{formData.title.length}/200 characters</p>
        </div>

        {/* Description */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <label className="block text-sm font-semibold mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of what your prompt does..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            rows={3}
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            What problem does this prompt solve? Keep it concise.
          </p>
        </div>

        {/* Prompt Text */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <label className="block text-sm font-semibold mb-2">
            Prompt Text <span className="text-red-500">*</span>
          </label>
          <ReactQuill
            theme="snow"
            value={formData.prompt_text}
            onChange={handleQuillChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Enter your AI prompt here..."
            className="bg-white"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-sm text-gray-500">
              The actual prompt text to use with AI models
            </p>
            <div className="flex items-center gap-2 text-sm">
              {analyzingTokens ? (
                <span className="text-gray-500">Counting tokens...</span>
              ) : (
                <span className="font-semibold text-boop-primary">{tokenCount} tokens</span>
              )}
            </div>
          </div>
        </div>

        {/* Examples */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <label className="block text-sm font-semibold mb-2">Examples (Optional)</label>
          <textarea
            name="examples"
            value={formData.examples}
            onChange={handleChange}
            placeholder="Example input/output pairs..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary font-mono text-sm"
            rows={6}
          />
          <p className="text-sm text-gray-500 mt-1">
            Show example inputs and expected outputs to help others understand your prompt
          </p>
        </div>

        {/* Category & Tags */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                required
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Target AI Model</label>
              <select
                name="target_ai_model"
                value={formData.target_ai_model}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
              >
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="claude-3-opus">Claude 3 Opus</option>
                <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                <option value="gemini-pro">Gemini Pro</option>
                <option value="llama-2">Llama 2</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="e.g., coding, python, data-analysis"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            />
            <p className="text-sm text-gray-500 mt-1">
              Add tags to help others find your prompt
            </p>
          </div>
        </div>

        {/* Visibility */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="w-5 h-5 text-boop-primary rounded focus:ring-2 focus:ring-boop-primary mr-3"
            />
            <div>
              <span className="font-semibold">Make this prompt public</span>
              <p className="text-sm text-gray-500">
                Public prompts appear in search results and can be rated by others
              </p>
            </div>
          </label>
        </div>

        {/* Rating Info */}
        {hasRatings && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <div className="flex items-start">
              <FaInfoCircle className="text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">This prompt has {originalPrompt?.total_ratings} rating{originalPrompt?.total_ratings !== 1 ? 's' : ''}</p>
                <p>
                  If you change the title, description, or prompt content, all ratings will be permanently deleted
                  and your prompt will start fresh with a rating of 0.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-6 py-3 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            <FaSave className="mr-2" />
            {loading ? 'Updating...' : 'Update Prompt'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/prompts/${id}`)}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPrompt;
